import { captureException } from '@sentry/react';
import { getStripeAccountIdByState } from 'domains/billing';
import { ContactRelationship } from 'domains/client';
import { scheduleEvaluationFromSignupV3, SignupSchedule } from 'domains/evaluations/evaluationSchedule';
import { PhysicianForm, SalesforcePayerTypes } from 'domains/signup/data/types';
import { DirectSignupFilters } from 'domains/signupDirect/context';
import { getInsurancePrimarySubscriberDetails } from 'domains/signupDirect/Evaluation/Insurance/eligibility';
import { mergeCommunicationConcerns } from 'domains/signupDirect/misc/communicationConcerns';
import {
  getEvaluationPrice,
  OUT_OF_NETWORK_EVALUATION_PRICE,
  PRIVATE_PAY_EVALUATION_PRICE,
} from 'domains/signupDirect/misc/evalPrice';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { getTempLeadId, setTempLeadId, clearTempLeadId } from 'domains/signupDirect/misc/leadStorage';
import { EvaluationFormType, getPayerTypeFromBillingInfo } from 'domains/signupDirect/schemas/evaluationSchema';
import { createSalesforceLead, updateSalesforceLead } from 'domains/signupv4/Leads/queries';
import { getSFInsuranceType } from 'domains/signupv4/Parent/actors';
import { getGCLIDParameters } from 'domains/tracking/GCLIDtracking';
import { getUTMParameters } from 'domains/tracking/UTMTracking';
import { get } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { UseFormSetError } from 'react-hook-form';
import { extractDate } from 'utils/date';
import { removeWhitespaces } from 'utils/phoneNumber';
import { PreBook } from 'domains/signupDirect/schemas/preBookSchema';
import { LeadDesiredPaymentMethod } from 'domains/signupv4/Leads/types';
import { SignupLeadV4 } from 'domains/signupv4/Leads/types';
import { DEFAULT_IN_NETWORK_ESTIMATES } from '../SyntaxComps/constants';
import { EvaluationDuration } from 'domains/calendars/types';
import { EvaluationScheduleFormType } from '../schemas/evaluationScheduleSchema';
import { SUBSCRIBER_RELATIONSHIP_CODE_MAP } from 'domains/signupDirect/businessLogic/relationships';

type SubmitParams = {
  data: EvaluationFormType;
  filters: DirectSignupFilters;
  onContinueToScheduleSts: (PreBook: PreBook) => void;
  setError: UseFormSetError<EvaluationFormType>;
  isResumedLead: boolean;
};

export const submitEvalForm = async (params: SubmitParams) => {
  if (params.data.billingInfo.type === 'privatePay') {
    return submitPrivatePay(params);
  }

  if (
    params.data.billingInfo.type === 'insurance-commercial' ||
    params.data.billingInfo.type === 'insurance-medicaid' ||
    params.data.billingInfo.type === 'insurance-medicare'
  ) {
    return submitInsurance(params);
  }
};

const submitPrivatePay = async ({ data, filters, onContinueToScheduleSts, setError, isResumedLead }: SubmitParams) => {
  if (data.billingInfo.type !== 'privatePay') throw "Expected billingInfo.type to be 'privatePay'";

  const UTMParams = getUTMParameters();
  const GCLIDParams = getGCLIDParameters();

  const { clientInfo, contactInfo, evaluationSchedule } = data;
  // Try to get leadId from form data first, then from localStorage as fallback
  let leadId = data.leadId ?? getTempLeadId();

  const leadObject = {
    communicationConcerns: data.concerns.clinicalConcernKeys.join(','),
    detectedAreas: data.concerns.clinicalAreas,
    leadSource: 'Self-Referral - Web Inquiry',
    desiredPaymentMethod:
      filters.paymentMethod.type === 'privatePay' ? 'self-pay' : ('Insurance' as LeadDesiredPaymentMethod),
    selfSignUp: true,
    address: {
      state: data.clientInfo.stateCode,
    },
    firstName: data.clientInfo.firstName,
    lastName: data.clientInfo.lastName,
    contactFirstName: data.contactInfo.firstName,
    contactLastName: data.contactInfo.lastName,
    email: data.contactInfo.email,
    dob: data.clientInfo.dateOfBirth,
    phone: data.contactInfo.phone,
    serviceFor: data.clientType,
    primaryLanguage: filters.language,
    relationshipToClient: data.contactInfo.relationshipToClient,
    evalAuthRequired: 'No',
    visitAuthRequired: 'No',
    physicianOrderRequired: 'No',
    insuranceType: SalesforcePayerTypes.SELF_PAY,
    signupFlowOutcome: 'Booked for Evaluation',
    sourceSystem: 'Signup Direct',
    contactTimezone: evaluationSchedule.timeZone,
    ...(data.evaluationSchedule.type === 'single'
      ? {
          evaluationDate: data.evaluationSchedule.timeSlot.time,
        }
      : {
          evaluationDate: data.evaluationSchedule.firstTimeSlot.time,
        }),
    ...UTMParams,
    ...GCLIDParams,
  } satisfies Partial<SignupLeadV4>;

  // Check if leadId already exists, update lead if it does
  // we don't need a new lead if we're resuming
  if (!leadId) {
    const leadResponse = await createSalesforceLead(leadObject);
    leadId = leadResponse.id;
    // Store leadId in localStorage for persistence across form submissions
    setTempLeadId(leadId);
  } else {
    await updateSalesforceLead({
      ...leadObject,
      id: leadId,
    });
  }

  // Get stripe data
  const stripeData = await getStripeAccountIdByState(filters.stateCode);

  const {
    contactInfo: { relationshipToClient },
  } = data;

  const schedules = getSchedulesPayload(evaluationSchedule);

  // Submit final form with schedule
  let scheduleResponse;
  try {
    scheduleResponse = await scheduleEvaluationFromSignupV3({
      isResumeFlow: isResumedLead,
      address: { state: filters.stateCode },
      amount: PRIVATE_PAY_EVALUATION_PRICE, // @TODO does BE even need this?
      clientFirstName: clientInfo.firstName,
      clientLastName: clientInfo.lastName,
      contactFirstName:
        data.contactInfo.relationshipToClient === ContactRelationship.Client
          ? data.clientInfo.firstName
          : (data.contactInfo.firstName as string),
      contactLastName:
        data.contactInfo.relationshipToClient === ContactRelationship.Client
          ? data.clientInfo.lastName
          : (data.contactInfo.lastName as string),
      contactTimeZone: evaluationSchedule.timeZone,
      schedules,
      schedulesTimezone: evaluationSchedule.timeZone,
      dob: extractDate(clientInfo.dateOfBirth),
      email: contactInfo.email,
      isClientMinor: filters.therapyTarget === 'child',
      phone: { mobile: removeWhitespaces(contactInfo.phone) },
      relationshipToClient: relationshipToClient,
      skipStripePayment: false,
      isCommercialInsurance: false,
      leadID: leadId as string,
      stripeAccountID: stripeData.accountId,
      stripePaymentMethodID: data.billingInfo.stripeMethodId,
    });
  } catch (e) {
    handleServerSideErrors({ data, filters, onContinueToScheduleSts, setError, isResumedLead }, e);

    return;
  }

  await trackSuccessfulEval(isResumedLead);

  // Clear temporary leadId from localStorage on successful submission
  clearTempLeadId();

  if (evaluationSchedule.type === 'split') {
    // No need to pre-book on splits
    window.location.href = scheduleResponse.invitationLink;
  } else if (evaluationSchedule.type === 'single') {
    // Redirect to pre-book
    if (scheduleResponse.invitationLink && scheduleResponse.createdClient.clientID) {
      onContinueToScheduleSts({
        leadId,
        invitationLink: scheduleResponse.invitationLink,
        clientID: scheduleResponse.createdClient.clientID,
        therapistEmail: scheduleResponse.createdClient.createdClient.careTeam.therapistEmail,
        therapistCalendarID: scheduleResponse.createdClient.createdClient.careTeam.acuityCalendarID,
        clientFirstName: scheduleResponse.createdClient.createdClient.client.clientFirstName,
        clientLastName: scheduleResponse.createdClient.createdClient.client.clientLastName,
        contactEmail: scheduleResponse.createdClient.createdClient.primaryContact.email,
        contactPhone: scheduleResponse.createdClient.createdClient.primaryContact.mobilePhone,
        contactTimeZone: scheduleResponse.createdClient.createdClient.primaryContact.contactTimeZone,
        userTimezone: scheduleResponse.createdClient.createdClient.careTeam.acuityTimeZone,
        billingType: data.billingInfo.type,
      });
    }
  }

  return scheduleResponse;
};

const submitInsurance = async ({ data, filters, onContinueToScheduleSts, setError, isResumedLead }: SubmitParams) => {
  if (
    data.billingInfo.type !== 'insurance-commercial' &&
    data.billingInfo.type !== 'insurance-medicaid' &&
    data.billingInfo.type !== 'insurance-medicare'
  ) {
    throw "Expected billingInfo.type to be 'insurance'";
  }

  const UTMParams = getUTMParameters();
  const GCLIDParams = getGCLIDParameters();

  const { clientInfo, contactInfo, evaluationSchedule } = data;
  // Try to get leadId from form data first, then from localStorage as fallback
  let leadId = data.leadId ?? getTempLeadId();
  let primaryPhysician: PhysicianForm | undefined;

  if (data.billingInfo.type === 'insurance-medicaid' || data.billingInfo.type === 'insurance-medicare') {
    primaryPhysician = {
      name: data.billingInfo.primaryPhysician.name ?? '',
      office: data.billingInfo.primaryPhysician.officeName ?? '',
      phoneNumber: data.billingInfo.primaryPhysician.phone ?? '',
      website: data.billingInfo.primaryPhysician.website ?? '',
    } as PhysicianForm;
  }

  const subscriberDetails = getInsurancePrimarySubscriberDetails(data);
  const leadObject = {
    address: {
      state: filters.stateCode,
    },
    email: contactInfo.email,
    firstName: clientInfo.firstName,
    lastName: clientInfo.lastName,
    contactFirstName: data.contactInfo.firstName,
    contactLastName: data.contactInfo.lastName,
    phone: contactInfo.phone,
    ...UTMParams,
    ...GCLIDParams,
    communicationConcerns: mergeCommunicationConcerns(data.concerns.clinicalConcernKeys, data.concerns.clinicalAreas),
    detectedAreas: data.concerns.clinicalAreas,
    leadSource: 'Self-Referral - Web Inquiry',
    primaryPhysician,
    desiredPaymentMethod: 'Insurance' as LeadDesiredPaymentMethod,
    selfSignUp: true,
    dob: data.clientInfo.dateOfBirth,
    serviceFor: data.clientType,
    primaryLanguage: filters.language,
    relationshipToClient: data.contactInfo.relationshipToClient,
    ...(evaluationSchedule.type === 'single'
      ? {
          evaluationDate: evaluationSchedule.timeSlot.time,
        }
      : {
          evaluationDate: evaluationSchedule.firstTimeSlot.time,
        }),
    evalAuthRequired: 'Yes',
    visitAuthRequired: 'Yes',
    physicianOrderRequired: 'Yes',
    memberID: data.billingInfo.memberId ?? '',
    insuranceType: getSFInsuranceType(data.billingInfo.insuranceData.insuranceType, false),
    subscriber: {
      dateOfBirth: subscriberDetails.dateOfBirth,
      firstName: subscriberDetails.firstName,
      lastName: subscriberDetails.lastName,
      relationshipToClient: SUBSCRIBER_RELATIONSHIP_CODE_MAP[subscriberDetails.relationShipToClient],
    },
    signupFlowOutcome: 'Booked for Evaluation',
    sourceSystem: 'Signup Direct',
    contactTimezone: evaluationSchedule.timeZone,
  } satisfies Partial<SignupLeadV4>;

  // Check if leadId already exists, update lead if it does
  // we don't need a new lead if we're resuming
  if (!leadId) {
    const leadResponse = await createSalesforceLead(leadObject);
    leadId = leadResponse.id;
    // Store leadId in localStorage for persistence across form submissions
    setTempLeadId(leadId);
  } else {
    await updateSalesforceLead({
      ...leadObject,
      id: leadId,
    });
  }

  const {
    contactInfo: { relationshipToClient },
  } = data;

  const stripeData = await getStripeAccountIdByState(filters.stateCode);

  // Submit final form with schedule
  let scheduleResponse;

  const isCommercialInsurance = data.billingInfo.type === 'insurance-commercial';
  const isInsuranceOutOfNetwork = data.billingInfo.insuranceData.networkStatus === 'Out-of-network';

  const commercialEvalPrice = getEvaluationPrice({
    isPrivatePay: false,
    insurance: data.billingInfo.insuranceData,
    isOutOfNetwork: data.billingInfo.insuranceData.networkStatus === 'Out-of-network',
  });

  // TODO: move this function to a utils file
  const getCoPayAmount = () => {
    if (data.billingInfo.type === 'insurance-commercial') {
      if (isInsuranceOutOfNetwork) {
        return OUT_OF_NETWORK_EVALUATION_PRICE;
      }

      return (
        data.billingInfo.insuranceData.averageSessionPatientResponsibility ??
        DEFAULT_IN_NETWORK_ESTIMATES.defaultDeposit
      );
    }

    return undefined;
  };

  const schedules = getSchedulesPayload(evaluationSchedule);

  try {
    scheduleResponse = await scheduleEvaluationFromSignupV3({
      isResumeFlow: isResumedLead,
      address: { state: filters.stateCode },
      schedules,
      schedulesTimezone: evaluationSchedule.timeZone,
      clientFirstName: clientInfo.firstName,
      clientLastName: clientInfo.lastName,
      contactFirstName:
        data.contactInfo.relationshipToClient === ContactRelationship.Client
          ? data.clientInfo.firstName
          : (data.contactInfo.firstName as string),
      contactLastName:
        data.contactInfo.relationshipToClient === ContactRelationship.Client
          ? data.clientInfo.lastName
          : (data.contactInfo.lastName as string),
      contactTimeZone: evaluationSchedule.timeZone,
      dob: extractDate(clientInfo.dateOfBirth),
      email: data.contactInfo.email,
      isClientMinor: filters.therapyTarget === 'child',
      phone: { mobile: removeWhitespaces(data.contactInfo.phone) },
      relationshipToClient: relationshipToClient,
      leadID: leadId as string,
      skipStripePayment: isCommercialInsurance ? false : true,
      isCommercialInsurance,
      isInsuranceOutOfNetwork,
      amount: isCommercialInsurance ? commercialEvalPrice : undefined, // @TODO
      coPay: getCoPayAmount(),
      entity: data.billingInfo.insuranceData.entity,
      primaryInsurance: {
        insuredFirstName: subscriberDetails.firstName,
        insuredLastName: subscriberDetails.lastName,
        insuredDob: new Date(subscriberDetails.dateOfBirth),
        insuredRelationshipToClient: subscriberDetails.relationShipToClient,
        insurancePayer: data.billingInfo.insuranceData.payerId,
        insurancePayerName: data.billingInfo.insuranceData.candidPayerName,
        insuredPayerType: getPayerTypeFromBillingInfo(data.billingInfo),
        insuredMemberID: data.billingInfo.memberId ?? '',
        insuredGroupID: data.billingInfo.groupId,
      },
      stripeAccountID: isCommercialInsurance ? stripeData.accountId : undefined,
      stripePaymentMethodID:
        data.billingInfo.type === 'insurance-commercial' ? data.billingInfo.stripeMethodId : undefined,
    });
  } catch (e) {
    handleServerSideErrors({ data, filters, onContinueToScheduleSts, setError, isResumedLead }, e);
    throw e;
  }

  await trackSuccessfulEval(isResumedLead);

  // Clear temporary leadId from localStorage on successful submission
  clearTempLeadId();

  const isInsuranceEvalOnly = data.billingInfo.insuranceData.evaluationOnly;
  // Redirect to password change when insurance is eval only
  if (scheduleResponse.invitationLink && isInsuranceEvalOnly) {
    window.location.href = scheduleResponse.invitationLink;
  }

  if (evaluationSchedule.type === 'split') {
    // No need to pre-book on splits
    window.location.href = scheduleResponse.invitationLink;
  } else if (evaluationSchedule.type === 'single') {
    // Redirect to pre-book
    if (scheduleResponse.invitationLink && !isInsuranceEvalOnly && scheduleResponse.createdClient.clientID) {
      onContinueToScheduleSts({
        leadId,
        invitationLink: scheduleResponse.invitationLink,
        clientID: scheduleResponse.createdClient.clientID,
        therapistEmail: scheduleResponse.createdClient.createdClient.careTeam.therapistEmail,
        therapistCalendarID: scheduleResponse.createdClient.createdClient.careTeam.acuityCalendarID,
        clientFirstName: scheduleResponse.createdClient.createdClient.client.clientFirstName,
        clientLastName: scheduleResponse.createdClient.createdClient.client.clientLastName,
        contactEmail: scheduleResponse.createdClient.createdClient.primaryContact.email,
        contactPhone: scheduleResponse.createdClient.createdClient.primaryContact.mobilePhone,
        contactTimeZone: scheduleResponse.createdClient.createdClient.primaryContact.contactTimeZone,
        userTimezone: scheduleResponse.createdClient.createdClient.careTeam.acuityTimeZone,
        billingType: data.billingInfo.type,
      });
    }
  }

  return scheduleResponse;
};

// @TODO(juan): extract this to a reusable function. For some reason it errors out if we do, need to investigate whats going on
const trackSuccessfulEval = (isResumedLead: boolean = false) => {
  return new Promise(s => {
    mixpanel.track(
      DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SUBMITTED,
      { isResumedLead },
      {
        send_immediately: true,
      },
      () => {
        s(true);
      },
    );
  });
};

const handleServerSideErrors = ({ data, filters, setError }: SubmitParams, e: unknown) => {
  // @TODO refactor, proper error types, solve issue with axios error types
  const errorMessage = get(e, 'originalError.response.data.message') ?? 'Something went wrong';
  const errorCode = get(e, 'originalError.response.data.error.code');

  // Ensure we're not losing the leadId when there's an error
  const leadId = data.leadId ?? getTempLeadId();

  setError(errorCode === 'card_declined' ? 'billingInfo.stripeMethodId' : 'root', {
    message: errorMessage,
  });

  if (errorCode !== 'card_declined') {
    captureException(new Error('[SignupConsolidated] :: Error submitting Evaluation form'), {
      data: {
        e,
        filters,
        data,
        leadId,
      },
    });
  }
};

const getSchedulesPayload = (evaluationSchedule: EvaluationScheduleFormType): SignupSchedule[] => {
  const schedules: SignupSchedule[] = [];
  if (evaluationSchedule.type === 'single') {
    schedules.push({
      duration: EvaluationDuration.Single,
      timeSlot: evaluationSchedule.timeSlot.time.toISOString(),
      calendarID: evaluationSchedule.timeSlot.calendarID.toString(),
    });
  } else if (evaluationSchedule.type === 'split') {
    schedules.push({
      duration: EvaluationDuration.Split,
      timeSlot: evaluationSchedule.firstTimeSlot.time.toISOString(),
      calendarID: evaluationSchedule.firstTimeSlot.calendarID.toString(),
    });

    if (evaluationSchedule.secondTimeSlot) {
      schedules.push({
        duration: EvaluationDuration.Split,
        timeSlot: evaluationSchedule.secondTimeSlot.time.toISOString(),
        calendarID: evaluationSchedule.secondTimeSlot.calendarID.toString(),
      });
    }
  }

  return schedules;
};
